/**
 * MilestoneCelebration — Phase 85.4
 *
 * A quiet celebration when a loan crosses halfway or gets cleared, with a
 * one-tap share for the person who paid it with you. Uses the native share
 * sheet when available (WhatsApp, Messages, Mail) and falls back to copying the
 * note — no social networks involved.
 */
import React, { useState } from 'react';
import { PartyPopper, Share2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/format';

const STAGE_COPY = {
  cleared: { title: 'Paid off', line: 'This loan is fully cleared.' },
  final_stretch: { title: 'Final stretch', line: 'Over 90% of this loan is behind you.' },
  three_quarters: { title: 'Three quarters done', line: 'You have repaid 75% of what you borrowed.' },
  halfway: { title: 'Halfway there', line: 'More than half of this loan is repaid.' },
};

export function MilestoneCelebration({ milestone, name }) {
  const [shared, setShared] = useState(false);
  if (!milestone?.stage) return null;

  const copy = STAGE_COPY[milestone.stage];
  if (!copy) return null;

  const ccy = milestone.currency || 'INR';
  const paid = milestone.paid_off_amount;
  const pct = milestone.progress_pct;

  const note = milestone.cleared
    ? `We've fully paid off the ${name}${paid ? ` — ${formatMoney(paid, ccy)} repaid` : ''}. Debt-free on this one!`
    : `${copy.title} on the ${name}: ${pct}% repaid${paid ? ` (${formatMoney(paid, ccy)})` : ''}.`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'A milestone worth sharing', text: note });
      } else {
        await navigator.clipboard.writeText(note);
        toast.success('Copied — paste it to whoever paid this off with you.');
      }
      setShared(true);
    } catch {
      /* user dismissed the share sheet — nothing to report */
    }
  };

  return (
    <div
      className="rounded-xl border border-evergreen/25 bg-evergreen/5 px-4 py-3.5 flex flex-wrap items-center justify-between gap-3"
      data-testid="liab-milestone"
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-charcoal flex items-center gap-1.5">
          <PartyPopper className="h-4 w-4 text-evergreen" aria-hidden="true" />
          <span data-testid="liab-milestone-title">{copy.title}</span>
          {pct != null && !milestone.cleared && (
            <span className="text-xs font-normal text-muted-foreground">· {pct}% repaid</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5" data-testid="liab-milestone-line">
          {copy.line}
          {paid ? ` ${formatMoney(paid, ccy)} repaid so far.` : ''}
        </p>
      </div>
      <Button size="sm" variant="secondary" onClick={share} data-testid="liab-milestone-share">
        {shared ? <Check className="h-4 w-4 mr-1.5" /> : <Share2 className="h-4 w-4 mr-1.5" />}
        {shared ? 'Shared' : 'Share with family'}
      </Button>
    </div>
  );
}

export default MilestoneCelebration;
